import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowUp } from "lucide-react";
import { cn } from "@/utils/helpers";
import { Avatar } from "../comments/CommentThread";
import type { BriefComment } from "@/types/productionBrief.type";

/**
 * The thread on a production brief, shared between the team and the client.
 *
 * Every comment carries the brief version it was written against, and the thread
 * is grouped by it. That grouping is the whole point: a brief goes out, comes
 * back declined, gets rewritten by hand, and goes out again — so a round-1
 * complaint about a hook that no longer exists must never read as a note on the
 * round-2 rewrite. The version separators are the load-bearing part of this file.
 *
 * The thread can sit inline under the brief or inside the brief activity island.
 */

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  const secs = Math.max(1, Math.round((Date.now() - then) / 1000));
  if (secs < 60) return "just now";
  const mins = Math.round(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}

function VersionSeparator({
  version,
  current,
}: {
  version: number;
  current: boolean;
}) {
  return (
    <li className="flex items-center gap-2 pt-1">
      <span
        className={cn(
          "shrink-0 text-[12.5px] tabular-nums",
          current ? "text-co-accent-ink" : "text-co-faint",
        )}
      >
        {current ? `Version ${version} · current` : `Version ${version}`}
      </span>
      <span className="h-px flex-1 bg-co-line" />
    </li>
  );
}

function CommentRow({ comment }: { comment: BriefComment }) {
  const client = comment.authorType === "CLIENT";
  return (
    <li className="flex flex-col gap-[5px]">
      <div className="flex min-w-0 items-center gap-2">
        <Avatar name={comment.authorName} size={20} />
        <span className="shrink-0 text-[13px] font-semibold text-co-text">
          {comment.authorName.split(/\s+/)[0]}
        </span>
        <span className="truncate text-[12.5px] text-co-muted">
          {client ? "Client" : "Mohtawa"}
        </span>
        <span className="ml-auto shrink-0 text-[12.5px] text-co-faint">
          {timeAgo(comment.createdAt)}
        </span>
      </div>
      <p
        dir="auto"
        className="whitespace-pre-wrap text-[13.5px] leading-[1.5] text-co-text"
      >
        {comment.body}
      </p>
    </li>
  );
}

export default function BriefComments({
  comments,
  onSubmit,
  submitting = false,
  readOnly = false,
  currentVersion,
  showHeader = true,
  frameless = false,
}: {
  comments: BriefComment[];
  onSubmit: (body: string) => void;
  submitting?: boolean;
  readOnly?: boolean;
  currentVersion: number;
  showHeader?: boolean;
  frameless?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const listRef = useRef<HTMLUListElement>(null);

  const groups = useMemo(() => {
    const byVersion = new Map<number, BriefComment[]>();
    for (const c of comments) {
      const bucket = byVersion.get(c.briefVersion);
      if (bucket) bucket.push(c);
      else byVersion.set(c.briefVersion, [c]);
    }
    const ordered = [...byVersion.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([version, list]) => ({
        version,
        comments: [...list].sort((a, b) =>
          a.createdAt.localeCompare(b.createdAt),
        ),
      }));
    if (ordered.length > 0 && !byVersion.has(currentVersion)) {
      ordered.push({ version: currentVersion, comments: [] });
    }
    return ordered;
  }, [comments, currentVersion]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [comments.length]);

  const submit = () => {
    const body = draft.trim();
    if (!body || submitting) return;
    onSubmit(body);
    setDraft("");
  };

  const countLabel =
    comments.length === 0
      ? "None yet"
      : comments.length === 1
        ? "1 comment"
        : `${comments.length} comments`;

  return (
    <div
      className={cn(
        "flex flex-col overflow-hidden",
        frameless ? "bg-transparent" : "rounded-card bg-co-surface",
      )}
    >
      {showHeader ? (
        <div className="flex items-center gap-1.5 px-3.5 pb-1 pt-3">
          <p className="min-w-0 flex-1 text-[13.5px] font-semibold text-co-text">
            Comments
          </p>
          <span className="shrink-0 text-[12px] tabular-nums text-co-muted">
            {countLabel}
          </span>
        </div>
      ) : null}

      {groups.length > 0 ? (
        <ul
          ref={listRef}
          className="custom-scrollbar flex max-h-60 flex-col gap-3 overflow-y-auto px-3.5 py-2.5"
        >
          {groups.map((group) => (
            <li key={group.version}>
              <ul className="flex flex-col gap-2.5">
                <VersionSeparator
                  version={group.version}
                  current={group.version === currentVersion}
                />
                {group.comments.length === 0 ? (
                  <li className="text-[12.5px] text-co-faint">
                    Nothing on this version yet.
                  </li>
                ) : (
                  group.comments.map((c) => (
                    <CommentRow key={c.id} comment={c} />
                  ))
                )}
              </ul>
            </li>
          ))}
        </ul>
      ) : null}

      {!readOnly && (
        <div className="px-3.5 pb-3.5 pt-2">
          <div className="co-field-shell co-glass-control flex items-center gap-2.5 rounded-card py-2 pl-3 pr-2 outline-2 outline-transparent transition-[outline-color] focus-within:outline-co-accent">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !e.nativeEvent.isComposing
                ) {
                  e.preventDefault();
                  submit();
                }
              }}
              rows={1}
              maxLength={2000}
              disabled={submitting}
              placeholder="Comment on the creative brief…"
              dir="auto"
              className="min-w-0 flex-1 resize-none bg-transparent text-[14px] leading-[1.5] text-co-text [field-sizing:content] placeholder:text-co-faint focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
            />
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !draft.trim()}
              aria-label={submitting ? "Sending" : "Comment"}
              className={cn(
                "grid size-7 shrink-0 place-items-center rounded-full transition-colors",
                draft.trim() && !submitting
                  ? "bg-co-accent-fill text-co-on-accent"
                  : "bg-co-tonal text-co-faint",
                "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent disabled:cursor-default",
              )}
            >
              <ArrowUp className="size-[15px]" strokeWidth={2} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
